import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Filter,
  MessageSquare,
  Edit3,
  Flag,
  Clock,
  User,
  ExternalLink,
  X,
  Loader2,
  AlertCircle,
  CheckCheck,
} from 'lucide-react';
import api from '../../utils/api';
import DataHealthList, { type DataHealthColumn } from './dataHealth/DataHealthList';

/**
 * DataIssuesPanel — Phase 2 admin dashboard.
 *
 * Lista de data_issues del cliente con filtros (status / type / fecha) +
 * drawer lateral con detalle, activity_log, comentarios internos y acciones
 * según el status (Aprobar / Rechazar / Resolver / Reabrir / Asignar).
 *
 * Spec: archive/PROPOSAL_DATA_ISSUES_MODAL_2026-05-11.md (sección F + G)
 */

interface Props {
  clientId: string;
  clientName?: string;
}

interface ActivityLogEntry {
  actor_uid: string;
  actor_name: string;
  action: string;
  from: string | null;
  to: string | null;
  note: string | null;
  timestamp: any; // Firestore Timestamp serialized
}

interface InternalComment {
  uid: string;
  name: string;
  text: string;
  timestamp: any;
}

interface ProductSnapshot {
  title: string | null;
  sitio: string | null;
  sale_price: number | null;
  url: string | null;
  imagen: string | null;
}

interface DataIssue {
  issueId: string;
  clientId: string;
  id_catalogo: string;
  type: 'report' | 'edit_proposal';
  issue_category: string | null;
  field: string;
  current_value: any;
  proposed_value: any;
  product_snapshot: ProductSnapshot | null;
  reporter_uid: string;
  reporter_name: string;
  reporter_email: string;
  reporter_role: string;
  reported_at: any;
  status: string;
  assigned_to_uid: string | null;
  assigned_to_name: string | null;
  resolution_note: string | null;
  resolved_at: any;
  resolved_by_name: string | null;
  applied_at: any;
  applied_patch_response: any;
  comment: string | null;
  activity_log: ActivityLogEntry[];
  internal_comments: InternalComment[];
  has_unread_admin: boolean;
}

interface Stats {
  total: number;
  open_count: number;
  pending_approval_count: number;
  unread_admin_count: number;
  by_status: Record<string, number>;
  by_type: { report: number; edit_proposal: number };
}

// ---- helpers ----

const STATUS_LABELS: Record<string, string> = {
  open: 'Abierto',
  in_triage: 'En triage',
  in_progress: 'En progreso',
  pending_approval: 'Pendiente aprobación',
  approved: 'Aprobado',
  applied: 'Aplicado',
  failed: 'Falló',
  resolved: 'Resuelto',
  wont_fix: 'No se va a arreglar',
  duplicate: 'Duplicado',
  rejected: 'Rechazado',
  closed: 'Cerrado',
  reopened: 'Reabierto',
};

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 border-blue-200',
  in_triage: 'bg-amber-100 text-amber-700 border-amber-200',
  in_progress: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  pending_approval: 'bg-pink-100 text-pink-700 border-pink-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  applied: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  resolved: 'bg-green-100 text-green-700 border-green-200',
  wont_fix: 'bg-gray-100 text-gray-700 border-gray-200',
  duplicate: 'bg-gray-100 text-gray-700 border-gray-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  closed: 'bg-gray-100 text-gray-700 border-gray-200',
  reopened: 'bg-blue-100 text-blue-700 border-blue-200',
};

const TERMINAL_STATUSES = new Set(['applied', 'rejected', 'closed', 'duplicate']);

function tsToDate(ts: any): Date | null {
  if (!ts) return null;
  if (typeof ts === 'string') {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  // Firestore Timestamp serialized: { _seconds, _nanoseconds } or { seconds, nanoseconds }
  if (typeof ts === 'object') {
    const seconds = ts._seconds ?? ts.seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
    if (ts.toMillis) {
      try { return new Date(ts.toMillis()); } catch { /* ignore */ }
    }
  }
  return null;
}

function fmtDateTime(ts: any): string {
  const d = tsToDate(ts);
  if (!d) return '—';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}

function fmtRelative(ts: any): string {
  const d = tsToDate(ts);
  if (!d) return '—';
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min}m`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h}h`;
  const days = Math.round(h / 24);
  if (days < 30) return `hace ${days}d`;
  return fmtDateTime(ts);
}

function StatusBadge({ status }: { status: string }) {
  const label = STATUS_LABELS[status] || status;
  const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${cls}`}>
      {label}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  if (type === 'edit_proposal') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-pink-50 text-pink-700 border border-pink-200">
        <Edit3 className="w-3 h-3" /> edit
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
      <Flag className="w-3 h-3" /> report
    </span>
  );
}

// ---- component ----

const DataIssuesPanel: React.FC<Props> = ({ clientId, clientName }) => {
  const [issues, setIssues] = useState<DataIssue[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [filterStatus, setFilterStatus] = useState<string>('all'); // 'all' | csv
  const [filterType, setFilterType] = useState<string>('all'); // 'all' | 'report' | 'edit_proposal'
  const [filterDays, setFilterDays] = useState<string>('30'); // '7' | '30' | '90' | 'all'

  // Orden del servidor (DataHealthList → onSort). El backend refetchea con estos params.
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);

  // Selección
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<DataIssue | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  // Estado del drawer (acción)
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [internalCommentDraft, setInternalCommentDraft] = useState('');
  const [resolveNoteDraft, setResolveNoteDraft] = useState('');

  const headers = useMemo(() => ({ 'x-client-id': clientId }), [clientId]);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.type = filterType;
      if (filterDays !== 'all') {
        const days = parseInt(filterDays, 10);
        params.dateFrom = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
      }
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortDir = sortDir || 'asc';
      }
      const res = await api.get(`/api/clients/${clientId}/data-issues`, { headers, params });
      setIssues(res.data?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Error cargando issues');
    } finally {
      setLoading(false);
    }
  }, [clientId, filterStatus, filterType, filterDays, sortBy, sortDir, headers]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get(`/api/clients/${clientId}/data-issues/stats`, { headers });
      setStats(res.data);
    } catch (e) {
      // silent
    }
  }, [clientId, headers]);

  const fetchDetail = useCallback(async (issueId: string) => {
    setSelectedLoading(true);
    setActionError(null);
    try {
      const res = await api.get(`/api/clients/${clientId}/data-issues/${issueId}`, { headers });
      setSelected(res.data);
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Error cargando detalle');
    } finally {
      setSelectedLoading(false);
    }
  }, [clientId, headers]);

  useEffect(() => {
    fetchIssues();
    fetchStats();
  }, [fetchIssues, fetchStats]);

  useEffect(() => {
    if (selectedId) {
      fetchDetail(selectedId);
      setInternalCommentDraft('');
      setResolveNoteDraft('');
    } else {
      setSelected(null);
    }
  }, [selectedId, fetchDetail]);

  // ---- acciones del drawer ----

  const refresh = useCallback(async () => {
    await Promise.all([fetchIssues(), fetchStats()]);
    if (selectedId) await fetchDetail(selectedId);
  }, [fetchIssues, fetchStats, fetchDetail, selectedId]);

  const handleApprove = async () => {
    if (!selected) return;
    if (!window.confirm(`¿Aplicar este cambio al catalog?\n\n${selected.field}: "${selected.current_value ?? ''}" → "${selected.proposed_value ?? ''}"`)) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.post(
        `/api/clients/${clientId}/data-issues/${selected.issueId}/approve`,
        { note: null },
        { headers }
      );
      await refresh();
    } catch (e: any) {
      setActionError(e?.response?.data?.details || e?.response?.data?.error || e?.message || 'Error al aprobar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    const note = window.prompt('Motivo del rechazo (requerido):', '');
    if (!note || !note.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.post(
        `/api/clients/${clientId}/data-issues/${selected.issueId}/reject`,
        { note: note.trim() },
        { headers }
      );
      await refresh();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Error al rechazar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selected) return;
    const note = resolveNoteDraft.trim() || window.prompt('Nota de resolución (requerida):', '') || '';
    if (!note.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.post(
        `/api/clients/${clientId}/data-issues/${selected.issueId}/resolve`,
        { note: note.trim() },
        { headers }
      );
      setResolveNoteDraft('');
      await refresh();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Error al resolver');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async () => {
    if (!selected) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.post(
        `/api/clients/${clientId}/data-issues/${selected.issueId}/reopen`,
        { note: null },
        { headers }
      );
      await refresh();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Error al reabrir');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selected) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.patch(
        `/api/clients/${clientId}/data-issues/${selected.issueId}`,
        { status: newStatus },
        { headers }
      );
      await refresh();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Error al cambiar status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddInternalComment = async () => {
    if (!selected || !internalCommentDraft.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.patch(
        `/api/clients/${clientId}/data-issues/${selected.issueId}`,
        { internal_comment: internalCommentDraft.trim() },
        { headers }
      );
      setInternalCommentDraft('');
      await refresh();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'Error al agregar comentario');
    } finally {
      setActionLoading(false);
    }
  };

  // ---- paginación local (client-side, sobre los issues cargados) ----
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Reset a página 0 cuando cambian filtros / orden.
  useEffect(() => {
    setPageIndex(0);
  }, [filterStatus, filterType, filterDays, sortBy, sortDir]);

  // Cambio de orden → el refetch lo dispara el cambio de sortBy/sortDir (dep de fetchIssues).
  const handleSort = useCallback((by: string, dir: 'asc' | 'desc') => {
    setSortBy(by);
    setSortDir(dir);
  }, []);

  const totalRows = issues.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageIssues = useMemo(
    () => issues.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [issues, pageIndex, pageSize]
  );

  // ---- config de columnas (idénticas a la tabla ad-hoc previa) ----
  const columns: DataHealthColumn<DataIssue>[] = [
    {
      key: 'severidad',
      header: '!',
      width: '2rem',
      align: 'center',
      render: (it) =>
        it.has_unread_admin ? (
          <span className="inline-block w-2 h-2 rounded-full bg-red-500" title="Sin leer" />
        ) : null,
    },
    {
      key: 'tipo',
      header: 'Tipo',
      width: '6rem',
      render: (it) => <TypeBadge type={it.type} />,
    },
    {
      key: 'field',
      header: 'Campo',
      width: '8rem',
      cellClassName: 'font-mono text-xs text-gray-700',
      render: (it) => it.field,
    },
    {
      key: 'producto',
      header: 'Producto',
      width: '16rem',
      render: (it) => (
        <>
          <div className="text-xs text-gray-900 truncate">{it.product_snapshot?.title || '—'}</div>
          <div className="text-[10px] text-gray-400 font-mono">id: {it.id_catalogo}</div>
        </>
      ),
    },
    {
      key: 'valor',
      header: 'Valor actual → Propuesto',
      width: '18rem',
      sortable: false,
      render: (it) => (
        <>
          <div className="text-xs">
            <span className="text-gray-500 line-through">{String(it.current_value ?? '—').slice(0, 40)}</span>
            {it.type === 'edit_proposal' && (
              <>
                <span className="text-gray-400 mx-1">→</span>
                <span className="text-gray-900 font-medium">{String(it.proposed_value ?? '—').slice(0, 40)}</span>
              </>
            )}
          </div>
          {it.issue_category && (
            <div className="text-[10px] text-amber-700 mt-0.5">{it.issue_category}</div>
          )}
        </>
      ),
    },
    {
      key: 'reporter',
      header: 'Reporter',
      width: '9rem',
      cellClassName: 'text-xs text-gray-700 truncate',
      render: (it) => it.reporter_name || it.reporter_email || '—',
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '9rem',
      render: (it) => <StatusBadge status={it.status} />,
    },
    {
      key: 'fecha',
      header: 'Fecha',
      width: '8rem',
      cellClassName: 'text-xs text-gray-500 tabular-nums',
      render: (it) => fmtRelative(it.reported_at),
    },
  ];

  // ---- render ----

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header con stats */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Data Issues {clientName && <span className="text-sm font-normal text-gray-500">· {clientName}</span>}
          </h2>
          {stats && (
            <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
              {stats.total} total · {stats.open_count} abiertos · {stats.pending_approval_count} pendientes · {stats.unread_admin_count} sin leer
            </p>
          )}
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refrescar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-gray-100 bg-gray-50">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
        >
          <option value="all">Todos los estados</option>
          <option value="open,in_triage,in_progress,pending_approval,reopened">Activos</option>
          <option value="open">Abiertos</option>
          <option value="pending_approval">Pendiente aprobación</option>
          <option value="in_triage">En triage</option>
          <option value="in_progress">En progreso</option>
          <option value="applied">Aplicados</option>
          <option value="resolved">Resueltos</option>
          <option value="rejected">Rechazados</option>
          <option value="failed">Fallidos</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
        >
          <option value="all">Todos los tipos</option>
          <option value="report">Reportes</option>
          <option value="edit_proposal">Propuestas de edición</option>
        </select>
        <select
          value={filterDays}
          onChange={(e) => setFilterDays(e.target.value)}
          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
        >
          <option value="7">Últimos 7 días</option>
          <option value="30">Últimos 30 días</option>
          <option value="90">Últimos 90 días</option>
          <option value="all">Todo el historial</option>
        </select>
        <span className="text-xs text-gray-500 ml-auto tabular-nums">
          {issues.length} resultado{issues.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="m-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Listing (DataHealthList: paginación top+bottom + columnas redimensionables) */}
      <div className="p-3">
        <DataHealthList<DataIssue>
          columns={columns}
          rows={pageIssues}
          getRowId={(it) => it.issueId}
          onRowClick={(it) => setSelectedId(it.issueId)}
          activeRowId={selectedId}
          listId="datahealth-dataissues"
          onSort={handleSort}
          sortBy={sortBy}
          sortDir={sortDir}
          loading={loading && issues.length === 0}
          emptyState={<span className="italic">Sin issues que coincidan con los filtros.</span>}
          pagination={{
            idPrefix: 'data-issues',
            pageIndex,
            pageSize,
            totalRows,
            totalPages,
            onPageChange: setPageIndex,
            onPageSizeChange: (newSize) => {
              setPageSize(newSize);
              setPageIndex(0);
            },
          }}
        />
      </div>

      {/* Drawer detail */}
      {selectedId && (
        <DetailDrawer
          issue={selected}
          loading={selectedLoading}
          actionLoading={actionLoading}
          actionError={actionError}
          internalCommentDraft={internalCommentDraft}
          setInternalCommentDraft={setInternalCommentDraft}
          resolveNoteDraft={resolveNoteDraft}
          setResolveNoteDraft={setResolveNoteDraft}
          onClose={() => setSelectedId(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onResolve={handleResolve}
          onReopen={handleReopen}
          onStatusChange={handleStatusChange}
          onAddComment={handleAddInternalComment}
        />
      )}
    </div>
  );
};

// ============================================================================
// Drawer detail (sidebar)
// ============================================================================

interface DetailDrawerProps {
  issue: DataIssue | null;
  loading: boolean;
  actionLoading: boolean;
  actionError: string | null;
  internalCommentDraft: string;
  setInternalCommentDraft: (v: string) => void;
  resolveNoteDraft: string;
  setResolveNoteDraft: (v: string) => void;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  onResolve: () => void;
  onReopen: () => void;
  onStatusChange: (s: string) => void;
  onAddComment: () => void;
}

const DetailDrawer: React.FC<DetailDrawerProps> = ({
  issue, loading, actionLoading, actionError,
  internalCommentDraft, setInternalCommentDraft,
  resolveNoteDraft, setResolveNoteDraft,
  onClose, onApprove, onReject, onResolve, onReopen, onStatusChange, onAddComment,
}) => {
  return (
    <div className="fixed inset-0 z-[9000] flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black bg-opacity-30" onClick={onClose} />
      {/* Drawer */}
      <aside className="w-full max-w-xl bg-white shadow-2xl overflow-y-auto border-l border-gray-200">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Issue #{issue?.issueId?.slice(0, 8) || '—'}
            </h3>
            {issue && (
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                <TypeBadge type={issue.type} />
                <StatusBadge status={issue.status} />
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
          </div>
        )}

        {issue && !loading && (
          <div className="p-4 space-y-5">
            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{actionError}</span>
              </div>
            )}

            {/* Producto */}
            <section>
              <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Producto</h4>
              <div className="flex gap-3">
                {issue.product_snapshot?.imagen ? (
                  <img
                    src={issue.product_snapshot.imagen}
                    alt=""
                    className="w-16 h-16 object-contain rounded border border-gray-200 bg-gray-50 flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-gray-300 flex-shrink-0">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 leading-snug">
                    {issue.product_snapshot?.title || '(sin título)'}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">id_catalogo: {issue.id_catalogo}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {issue.product_snapshot?.sitio || '—'}
                    {issue.product_snapshot?.sale_price != null && (
                      <span className="ml-2 tabular-nums">
                        ${Number(issue.product_snapshot.sale_price).toLocaleString('es-AR')}
                      </span>
                    )}
                  </p>
                  {issue.product_snapshot?.url && (
                    <a
                      href={issue.product_snapshot.url}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1"
                    >
                      <ExternalLink className="w-3 h-3" /> abrir URL
                    </a>
                  )}
                </div>
              </div>
            </section>

            {/* Valor */}
            <section>
              <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                {issue.type === 'edit_proposal' ? 'Propuesta de edición' : 'Reporte'}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
                <div className="text-xs">
                  <span className="text-gray-500">Campo:</span>{' '}
                  <span className="font-mono text-gray-900">{issue.field}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-gray-500 mb-0.5">Actual</div>
                    <div className="font-mono bg-white border border-gray-200 rounded px-2 py-1 break-all">
                      {String(issue.current_value ?? '—')}
                    </div>
                  </div>
                  {issue.type === 'edit_proposal' && (
                    <div>
                      <div className="text-gray-500 mb-0.5">Propuesto</div>
                      <div className="font-mono bg-pink-50 border border-pink-200 rounded px-2 py-1 break-all">
                        {String(issue.proposed_value ?? '—')}
                      </div>
                    </div>
                  )}
                </div>
                {issue.issue_category && (
                  <div className="text-xs">
                    <span className="text-gray-500">Categoría:</span>{' '}
                    <span className="text-amber-700">{issue.issue_category}</span>
                  </div>
                )}
                {issue.comment && (
                  <div className="text-xs">
                    <div className="text-gray-500 mb-0.5">Comentario del reporter</div>
                    <div className="text-gray-800 bg-white border border-gray-200 rounded px-2 py-1.5 whitespace-pre-wrap">
                      {issue.comment}
                    </div>
                  </div>
                )}
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {issue.reporter_name} ({issue.reporter_role}) ·{' '}
                  <Clock className="w-3 h-3" /> {fmtDateTime(issue.reported_at)}
                </div>
              </div>
            </section>

            {/* Acciones */}
            <section>
              <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Acciones</h4>
              <ActionButtons
                issue={issue}
                actionLoading={actionLoading}
                resolveNoteDraft={resolveNoteDraft}
                setResolveNoteDraft={setResolveNoteDraft}
                onApprove={onApprove}
                onReject={onReject}
                onResolve={onResolve}
                onReopen={onReopen}
                onStatusChange={onStatusChange}
              />
            </section>

            {/* Comentarios internos */}
            <section>
              <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Comentarios internos (admin)
              </h4>
              <div className="space-y-2 mb-2">
                {(issue.internal_comments || []).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Sin comentarios internos aún.</p>
                ) : (
                  issue.internal_comments.map((c, i) => (
                    <div key={i} className="text-xs bg-amber-50 border border-amber-100 rounded p-2">
                      <div className="flex items-center justify-between text-gray-500 mb-0.5">
                        <span className="font-medium text-gray-700">{c.name}</span>
                        <span className="tabular-nums">{fmtRelative(c.timestamp)}</span>
                      </div>
                      <div className="text-gray-800 whitespace-pre-wrap">{c.text}</div>
                    </div>
                  ))
                )}
              </div>
              <textarea
                value={internalCommentDraft}
                onChange={(e) => setInternalCommentDraft(e.target.value)}
                placeholder="Comentario interno (no visible al reporter)..."
                rows={2}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={onAddComment}
                disabled={actionLoading || !internalCommentDraft.trim()}
                className="mt-1 px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Agregar comentario interno
              </button>
            </section>

            {/* Activity log */}
            <section>
              <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Activity log
              </h4>
              <ol className="space-y-1.5 border-l-2 border-gray-200 pl-3">
                {(issue.activity_log || []).map((e, i) => (
                  <li key={i} className="text-xs">
                    <div className="flex items-baseline gap-2">
                      <span className="text-gray-500 tabular-nums">{fmtDateTime(e.timestamp)}</span>
                      <span className="font-medium text-gray-800">{e.actor_name}</span>
                    </div>
                    <div className="text-gray-700">
                      <span className="font-mono text-[10px] text-gray-500 mr-1">{e.action}</span>
                      {e.from && e.to && <span>{e.from} → {e.to}</span>}
                      {!e.from && e.to && <span>→ {e.to}</span>}
                      {e.note && <span className="text-gray-600 italic"> · "{e.note}"</span>}
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Resolución info */}
            {(issue.resolution_note || issue.resolved_at) && (
              <section className="bg-gray-50 border border-gray-200 rounded p-3">
                <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Resolución</h4>
                {issue.resolved_by_name && (
                  <p className="text-xs text-gray-700">
                    Por <strong>{issue.resolved_by_name}</strong> el {fmtDateTime(issue.resolved_at)}
                  </p>
                )}
                {issue.resolution_note && (
                  <p className="text-xs text-gray-800 mt-1 whitespace-pre-wrap">{issue.resolution_note}</p>
                )}
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

// ============================================================================
// Action buttons (vary by status)
// ============================================================================

interface ActionButtonsProps {
  issue: DataIssue;
  actionLoading: boolean;
  resolveNoteDraft: string;
  setResolveNoteDraft: (v: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onResolve: () => void;
  onReopen: () => void;
  onStatusChange: (s: string) => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  issue, actionLoading, resolveNoteDraft, setResolveNoteDraft,
  onApprove, onReject, onResolve, onReopen, onStatusChange,
}) => {
  const isTerminal = TERMINAL_STATUSES.has(issue.status);
  const isEdit = issue.type === 'edit_proposal';
  const isReport = issue.type === 'report';

  if (isTerminal || issue.status === 'resolved' || issue.status === 'wont_fix' || issue.status === 'failed') {
    return (
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={onReopen}
          disabled={actionLoading || issue.status === 'applied'}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          title={issue.status === 'applied' ? 'No se puede reabrir un edit ya aplicado al catalog' : ''}
        >
          <RefreshCw className="w-3.5 h-3.5" /> Reabrir
        </button>
        {issue.status === 'failed' && isEdit && (
          <button
            onClick={onApprove}
            disabled={actionLoading}
            className="px-3 py-1.5 text-sm text-white bg-pink-600 rounded hover:bg-pink-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Reintentar aprobación
          </button>
        )}
      </div>
    );
  }

  // edit_proposal en estado activo → Aprobar / Rechazar
  if (isEdit) {
    return (
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={onApprove}
          disabled={actionLoading}
          className="px-3 py-1.5 text-sm text-white bg-pink-600 rounded hover:bg-pink-700 disabled:opacity-50 flex items-center gap-1.5"
        >
          {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Aprobar y aplicar
        </button>
        <button
          onClick={onReject}
          disabled={actionLoading}
          className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
        >
          <XCircle className="w-3.5 h-3.5" /> Rechazar
        </button>
        {issue.status === 'open' && (
          <button
            onClick={() => onStatusChange('in_triage')}
            disabled={actionLoading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Marcar en triage
          </button>
        )}
      </div>
    );
  }

  // report
  if (isReport) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2 flex-wrap">
          {issue.status === 'open' && (
            <button
              onClick={() => onStatusChange('in_triage')}
              disabled={actionLoading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Triagear
            </button>
          )}
          {(issue.status === 'in_triage' || issue.status === 'open') && (
            <button
              onClick={() => onStatusChange('in_progress')}
              disabled={actionLoading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              En progreso
            </button>
          )}
          <button
            onClick={onReject}
            disabled={actionLoading}
            className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
          >
            <XCircle className="w-3.5 h-3.5" /> Rechazar
          </button>
        </div>
        <div>
          <textarea
            value={resolveNoteDraft}
            onChange={(e) => setResolveNoteDraft(e.target.value)}
            placeholder="Nota de resolución (requerida para marcar resuelto)…"
            rows={2}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
          />
          <button
            onClick={onResolve}
            disabled={actionLoading || !resolveNoteDraft.trim()}
            className="mt-1 px-3 py-1.5 text-sm text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <CheckCheck className="w-3.5 h-3.5" /> Marcar resuelto
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default DataIssuesPanel;
